// RetailerDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './ChatApp.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Simple icon stubs
const DollarIcon = () => <span>$</span>;
const OrdersIcon = () => <span>📦</span>;
const PercentIcon = () => <span>%</span>; 

// Helper component for KPI cards
const KpiCard = ({ title, value, icon }) => (
    <div className={styles.kpiCard}>
        <div className={styles.kpiIcon}>{icon}</div>
        <div className={styles.kpiInfo}>
            <div className={styles.kpiLabel}>{title}</div>
            <div className={styles.kpiValue}>{value}</div>
        </div>
    </div>
);

// Helper component for tables
const AnalyticsTable = ({ title, data, columns }) => {
    if (!data || data.length === 0) return null;

    return (
        <div className={styles.adminCard}>
            <h3 className={styles.adminSectionTitle}>{title}</h3>
            <div className={styles.tableResponsive}>
                <table className={styles.analyticsTable}>
                    <thead>
                        <tr>
                            {columns.map(col => <th key={col.key}>{col.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={index}>
                                {columns.map(col => (
                                    <td key={`${col.key}-${index}`} data-label={col.label}>
                                        {col.isCurrency ? `$${Number(row[col.key] || 0).toFixed(2)}` : row[col.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const RetailerDashboard = ({ currentUser }) => {
    const [analytics, setAnalytics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchAnalytics = async () => {
        if (!currentUser || !currentUser.store_info) return;
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}/analytics/retailer/${currentUser.store_info.id}`);
            setAnalytics(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to fetch analytics.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);
    
    const storeName = currentUser.store_info?.name || "Your";

    return (
        <div className={styles.adminPageContainer}>
            <div className={styles.adminHeader}>
                <h2 className={styles.adminPageTitle}>{storeName} Dashboard</h2>
                <button onClick={fetchAnalytics} disabled={isLoading} className={styles.refreshButton}>
                    {isLoading ? 'Refreshing...' : 'Refresh Data'}
                </button>
            </div>
            
            {isLoading && <p>Loading analytics...</p>}
            {error && <div className={styles.adminError}>{error}</div>}

            {analytics && (
                <>
                    {/* --- KPI Stat Cards --- */}
                    <div className={styles.kpiGrid}>
                        <KpiCard title="Total Orders (Month)" value={analytics.total_orders_month} icon={<OrdersIcon />} />
                        <KpiCard title="Total Orders (YTD)" value={analytics.total_orders_ytd} icon={<OrdersIcon />} />
                        <KpiCard title="Spending (Month)" value={`$${analytics.spending_summary_month.toFixed(2)}`} icon={<DollarIcon />} />
                        <KpiCard title="Spending (YTD)" value={`$${analytics.spending_summary_ytd.toFixed(2)}`} icon={<DollarIcon />} />
                        <KpiCard title="Reorder Rate" value={`${analytics.reorder_rate_pct.toFixed(1)}%`} icon={<PercentIcon />} />
                    </div>

                    {/* --- Data Grid --- */}
                    <div className={styles.pageGrid}>
                        <AnalyticsTable
                            title="Most Ordered Products (by Quantity)"
                            data={analytics.most_ordered_products}
                            columns={[
                                { key: 'product_name', label: 'Product Name' },
                                { key: 'sku', label: 'SKU' },
                                { key: 'total_quantity', label: 'Total Quantity' },
                            ]}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default RetailerDashboard;